import { useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ProjectCalculator } from '@features/projects/components/ProjectWorkspace';
import { ProjectManager } from '@features/projects/components/ProjectManager';
import { PersonalDashboard } from '@features/dashboard/components/PersonalDashboard';
import { FinancialInsights } from '@features/payments/components/FinancialInsights';
import { PayoutQueuePage } from '@features/payouts/components/PayoutQueuePage';
import { ReceiptQueuePage } from '@features/payouts/components/ReceiptQueuePage';
import { CRMPage } from '@features/crm/components/CRMPage';
import { ConnectsLayout } from '@features/connects/components/ConnectsLayout';
import { ConnectsDashboard } from '@features/connects/components/ConnectsDashboard';
import { ContactsList } from '@features/connects/components/ContactsList';
import { CompaniesList } from '@features/connects/components/CompaniesList';
import { ContactDetail } from '@features/connects/components/ContactDetail';
import { CompanyDetail } from '@features/connects/components/CompanyDetail';
import { DigestArchive } from '@features/connects/components/DigestArchive';
import { ProspectsLayout } from '@features/prospects/components/ProspectsLayout';
import { ProspectsList } from '@features/prospects/components/ProspectsList';
import { ProspectDetail } from '@features/prospects/components/ProspectDetail';
import { JourneyLayout } from '@features/journey/components/JourneyLayout';
import { JourneyBoard } from '@features/journey/components/JourneyBoard';
import { JourneyDetail } from '@features/journey/components/JourneyDetail';
import { ProfilePage } from '@features/profile/components/ProfilePage';
import { TeamPage } from '@features/team/components/TeamPage';
import { InternAdminPage } from '@features/intern-admin/components/InternAdminPage';
import { ReportsPage } from '@features/reports/components/ReportsPage';
import { ReportView } from '@features/reports/components/ReportView';
import { BillingPage } from '@features/billing/components/BillingPage';
import { DocumentsIndexPage } from '@features/documents/components/DocumentsIndexPage';
import { AppLayout } from '@shared/components/layout/AppLayout';
import { LoginPage } from '@shared/components/auth/LoginPage';
import { AuthCallback } from '@shared/components/auth/AuthCallback';
import { HubCallback } from '@shared/components/auth/HubCallback';
import { SetPasswordPage } from '@shared/components/auth/SetPasswordPage';
import { PublicDocumentReader } from '@features/documents/components/PublicDocumentReader';
import { ProtectedRoute } from '@shared/components/auth/ProtectedRoute';
import { RequirePermission } from '@shared/components/auth/RequirePermission';
import { usePermissions } from '@/hooks/usePermissions';
import { InternHub } from '@features/intern/components/InternHub';
import { DashboardPage as InternDashboard } from '@features/intern/components/DashboardPage';
import { TaskDetailPage } from '@features/intern/components/TaskDetailPage';
import { SkillDetailPage } from '@features/intern/components/SkillDetailPage';
import { TimeLogPage } from '@features/intern/components/TimeLogPage';
import { WeekPage as InternWeek } from '@features/intern/components/WeekPage';
import { DonePage as InternDone } from '@features/intern/components/DonePage';
import { InternProtectedRoute } from '@features/intern/components/InternProtectedRoute';
import { OwnerSettingsProvider } from './contexts/OwnerSettingsContext';
import { ProjectProvider, useProjects } from './contexts/ProjectContext';
import { Toaster } from '@/components/ui/sonner';
import { useIsMobile } from '@/components/ui/use-mobile';
import { Project } from './types/project';
import { slugify } from './utils/slug';

// Dashboard page wrapper (Company Portfolio - for admins/owners)
function DashboardPage() {
  const navigate = useNavigate();
  const { projects, deleteProject, refreshProjects } = useProjects();

  return (
    <ProjectManager
      projects={projects}
      onSelectProject={(project) => {
        if (project) {
          navigate(`/projects/${slugify(project.projectName)}`);
        } else {
          navigate('/projects/new');
        }
      }}
      onDeleteProject={deleteProject}
      selectedProject={null}
      onRefresh={refreshProjects}
      onViewPayments={() => navigate('/projects/payments?tab=payments')}
    />
  );
}

// Personal Dashboard page wrapper (for specialists/external partners)
function PersonalDashboardPage() {
  const navigate = useNavigate();
  const { projects } = useProjects();

  return (
    <PersonalDashboard
      projects={projects}
      onSelectProject={(project) => {
        navigate(`/projects/${slugify(project.projectName)}`);
      }}
    />
  );
}

// Home fallback for users without portfolio access: interns go to the Intern OS,
// pipeline managers to the prospect pipeline (they have no projects surface at
// all, so /my-dashboard would be empty for them), everyone else (team members,
// partners) to their personal dashboard.
function NonAdminHome() {
  const { isIntern, isPipelineManager } = usePermissions();
  if (isIntern) return <Navigate to="/intern" replace />;
  if (isPipelineManager) return <Navigate to="/prospects" replace />;
  return <Navigate to="/my-dashboard" replace />;
}

// Project Calculator page wrapper
function ProjectPage() {
  const navigate = useNavigate();
  const { projectSlug } = useParams();
  const { projects, saveProject } = useProjects();

  // Find project by slug, fallback to ID for backwards compatibility
  const project = projectSlug
    ? projects.find(p => slugify(p.projectName) === projectSlug) || projects.find(p => p.id === projectSlug)
    : null;

  const handleSaveProject = useCallback(async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>, userEmail?: string) => {
    await saveProject(projectData, project?.id, userEmail);
  }, [saveProject, project?.id]);

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      <ProjectCalculator
        project={project || null}
        onSaveProject={handleSaveProject}
        onBackToList={() => navigate('/')}
      />
    </div>
  );
}

// Main App component - pure routing
export default function App() {
  // Toasts at the top on phones so they never fight the bottom tab bar
  const isMobile = useIsMobile();
  return (
    <ProjectProvider>
      <OwnerSettingsProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/hub-callback" element={<HubCallback />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
          {/* Public shared-document reader (no login) */}
          <Route path="/p/:token" element={<PublicDocumentReader />} />

          {/* Protected routes with layout */}
          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            {/* Profile - accessible by all authenticated users */}
            <Route path="/profile" element={<ProfilePage />} />

            {/* Company Portfolio - only for admins/owners who can see all projects */}
            <Route path="/" element={
              <RequirePermission
                permission={(ctx) => ctx.canViewAllProjects}
                fallback={<NonAdminHome />}
              >
                <DashboardPage />
              </RequirePermission>
            } />

            {/* Personal Dashboard - for all users who can view projects */}
            <Route path="/my-dashboard" element={
              <RequirePermission permission="canViewProjects" fallback={<Navigate to="/profile" replace />}>
                <PersonalDashboardPage />
              </RequirePermission>
            } />
            <Route path="/projects/new" element={
              <RequirePermission permission="canEditProjects" fallback={<Navigate to="/" replace />}>
                <ProjectPage />
              </RequirePermission>
            } />
            <Route path="/projects/:projectSlug" element={
              <RequirePermission permission="canViewProjects" fallback={<Navigate to="/" replace />}>
                <ProjectPage />
              </RequirePermission>
            } />

            {/* CRM - requires canViewCRM */}
            <Route path="/crm" element={
              <RequirePermission permission="canViewCRM" fallback={<Navigate to="/" replace />}>
                <CRMPage />
              </RequirePermission>
            } />

            {/* Financial Insights / Payments */}
            <Route path="/projects/payments" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <FinancialInsights />
              </RequirePermission>
            } />
            {/* Legacy route redirect */}
            <Route path="/insights" element={<Navigate to="/projects/payments" replace />} />

            {/* Payment Queue — the approval surface. super_admin only, matching the API. */}
            <Route path="/payouts" element={
              <RequirePermission permission="canMarkAsPaid" fallback={<Navigate to="/" replace />}>
                <PayoutQueuePage />
              </RequirePermission>
            } />

            {/* Receipt resolution — where a human says which MONTHS a payment
                earned. Same permission as payouts: it decides where money goes. */}
            <Route path="/receipts" element={
              <RequirePermission permission="canMarkAsPaid" fallback={<Navigate to="/" replace />}>
                <ReceiptQueuePage />
              </RequirePermission>
            } />

            {/* Weekly Reports - visible to admins/owners */}
            <Route path="/reports" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <ReportsPage />
              </RequirePermission>
            } />
            <Route path="/reports/:reportId" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <ReportView />
              </RequirePermission>
            } />

            {/* Documents (shared pages) - admins/owners only */}
            <Route path="/documents" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <DocumentsIndexPage />
              </RequirePermission>
            } />

            {/* Billing & Cash - admins/owners only */}
            <Route path="/billing" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <BillingPage />
              </RequirePermission>
            } />

            {/* Team Management - requires canManageUsers (super_admin only) */}
            <Route path="/team" element={
              <RequirePermission permission="canManageUsers" fallback={<Navigate to="/" replace />}>
                <TeamPage />
              </RequirePermission>
            } />

            {/* Intern Admin Console - super_admin only */}
            <Route path="/interns" element={
              <RequirePermission permission="canManageInterns" fallback={<Navigate to="/" replace />}>
                <InternAdminPage />
              </RequirePermission>
            } />

            {/* Connects — owner + super_admin only */}
            <Route path="/connects" element={
              <RequirePermission permission="canViewAllProjects" fallback={<Navigate to="/" replace />}>
                <ConnectsLayout />
              </RequirePermission>
            }>
              <Route index element={<ConnectsDashboard />} />
              <Route path="contacts" element={<ContactsList />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="companies" element={<CompaniesList />} />
              <Route path="companies/:id" element={<CompanyDetail />} />
              <Route path="digests" element={<DigestArchive />} />
            </Route>

            {/* Prospects — pipeline_manager + owner + super_admin */}
            <Route path="/prospects" element={
              <RequirePermission permission="canManagePipeline" fallback={<Navigate to="/" replace />}>
                <ProspectsLayout />
              </RequirePermission>
            }>
              <Route index element={<ProspectsList />} />
              <Route path=":id" element={<ProspectDetail />} />
            </Route>

            {/* Journey — the intro→close pipeline, same audience as Prospects */}
            <Route path="/journey" element={
              <RequirePermission permission="canManagePipeline" fallback={<Navigate to="/" replace />}>
                <JourneyLayout />
              </RequirePermission>
            }>
              <Route index element={<JourneyBoard />} />
              <Route path=":id" element={<JourneyDetail />} />
            </Route>
          </Route>

          {/* ─── Intern OS — own layout, no sidebar ─── */}
          {/* Gated: intern, owner, super_admin roles only */}
          {/* Hub home — choose your track */}
          <Route path="/intern" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <InternHub />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          {/* The daily operating dashboard — scoped per intern */}
          <Route path="/intern/dashboard" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <InternDashboard />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          {/* Back-compat: the old per-track URL now points at the shared dashboard */}
          <Route path="/intern/creative" element={<Navigate to="/intern/dashboard" replace />} />
          {/* This Week — weekly view derived from due dates + recurring checklist */}
          <Route path="/intern/week" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <InternWeek />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          {/* Done — completed tasks + explored skills */}
          <Route path="/intern/done" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <InternDone />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          <Route path="/intern/task/:taskId" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <TaskDetailPage />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          <Route path="/intern/skill/:skillId" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <SkillDetailPage />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />
          <Route path="/intern/time" element={
            <ProtectedRoute>
              <InternProtectedRoute>
                <TimeLogPage />
              </InternProtectedRoute>
            </ProtectedRoute>
          } />

          {/* 404 catch-all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <ErrorBanner />
        <Toaster richColors position={isMobile ? 'top-center' : 'bottom-right'} />
      </OwnerSettingsProvider>
    </ProjectProvider>
  );
}

// Global error display
function ErrorBanner() {
  const { error, setError, refreshProjects } = useProjects();
  if (!error) return null;

  return (
    <div className="fixed bottom-4 right-4 max-w-md p-4 bg-destructive/10 border border-destructive/20 rounded-lg shadow-lg z-50">
      <p className="text-destructive text-sm font-medium mb-2">{error}</p>
      <div className="flex gap-3">
        <button
          onClick={() => {
            setError(null);
            refreshProjects();
          }}
          className="text-destructive text-xs font-medium hover:underline"
        >
          Retry
        </button>
        <button
          onClick={() => setError(null)}
          className="text-destructive/70 text-xs hover:underline"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
